'use no memo';

/* eslint-disable complexity */
import { pick } from 'lodash-es';

import { isCateringOrderFlow, isYelpPlatform } from '@local/utils';

import { useSelectedAddressStore } from 'store';

import { getChannelInfoForOrderMeta } from 'helpers/customer';
import { FULFILLMENT_METHODS } from 'helpers/order';
import { getYelpData } from 'helpers/yelp';

import { UserDataType } from 'types/customer';
import { Customer, Tips } from 'types/order';
import { OnAddItemType, OnAddItemTypeModifierCategoryType } from 'types/menu';

import { LocalOrderType } from '.';

export type BuildOrderPayloadOrderType = LocalOrderType & {
  menu_id: string;
};

export type OrderPayloadCustomer = Pick<
  UserDataType,
  'email' | 'first_name' | 'last_name'
> & {
  id?: string | null | undefined;
  delivery?: Customer['delivery'] | null;
  curbside?: Customer['curbside'] | null;
  dine_in?: Customer['dine_in'] | null;
  rewards_opt_in?: boolean;
  phone?: { number: string };
  billing?: Record<string, unknown> | null;
  restaurant_marketing_consent?: {
    email_opt_in?: boolean;
    sms_opt_in?: boolean;
  };
};

export type BuildOrderPayload = {
  fulfill_method: BuildOrderPayloadOrderType['fulfill_method'];
  is_catering: boolean;
  items: Array<ReturnType<typeof serializeItem>>;
  special_instructions: null;
  tip: BuildOrderPayloadOrderType['tip'] | null;
  promo_code: BuildOrderPayloadOrderType['promo_code'];
  restaurant_id: BuildOrderPayloadOrderType['restaurant_id'];
  when: BuildOrderPayloadOrderType['when'];
  menu_id: BuildOrderPayloadOrderType['menu_id'];
  meta: {
    channel: ReturnType<typeof getChannelInfoForOrderMeta>;
    marketplace?: { id: string; x_id: string };
  };
  tips?: {
    managed_delivery_tip: Tips['managed_delivery_tip'] | null;
    restaurant_tip: Tips['restaurant_tip'] | null;
  } | null;
  include_single_use_items: BuildOrderPayloadOrderType['include_single_use_items'];
  customer?: OrderPayloadCustomer | null;
  estimated_fulfillment_time?: string;
  opportunity_token?: string;
};

function removeEmptyModifierCategories(
  modifierCategories: OnAddItemTypeModifierCategoryType[]
): OnAddItemTypeModifierCategoryType[] {
  return modifierCategories
    .filter(
      (modifierCategory) =>
        modifierCategory.modifiers && modifierCategory.modifiers.length
    )
    .map((modifierCategory) => ({
      ...modifierCategory,
      modifiers: modifierCategory.modifiers.map((modifier) =>
        modifier.modifier_categories
          ? {
              ...modifier,
              modifier_categories: removeEmptyModifierCategories(
                modifier.modifier_categories as OnAddItemTypeModifierCategoryType[]
              ),
            }
          : modifier
      ),
    }));
}

export function serializeItem(item: OnAddItemType) {
  return {
    id: item.sizeId || item.id,
    quantity: item.quantity,
    recipient: null,
    special_instructions: item.specialInstructions || null,
    client_id: (new Date().valueOf() * 1000).toString(),
    modifier_categories: removeEmptyModifierCategories(item.modifierCategories),
  };
}

export default function buildOrderPayload(
  order: BuildOrderPayloadOrderType,
  user: UserDataType
) {
  const channel = getChannelInfoForOrderMeta(order.restaurant_id);
  const selectedAddress = useSelectedAddressStore.getState().address;

  const orderPayload: BuildOrderPayload = {
    fulfill_method: order.fulfill_method,
    is_catering: isCateringOrderFlow(),
    items: (order.items || []).map(serializeItem),
    special_instructions: null,
    // TODO: remove 'tip' when we switch exclusively to /validate + POST /order v6:
    // https://chownow.atlassian.net/browse/CN-35607, https://chownow.atlassian.net/browse/CN-35606
    tip: order.tip || null,
    promo_code: order.promo_code || null,
    restaurant_id: order.restaurant_id,
    when: order.when,
    menu_id: order.menu_id,
    meta: { channel },
    tips: {
      managed_delivery_tip: order?.tips?.managed_delivery_tip || null,
      restaurant_tip: order?.tips?.restaurant_tip || null,
    },
    include_single_use_items: !!order.include_single_use_items,
    customer: null,
  };

  if (
    order.fulfill_method === FULFILLMENT_METHODS.delivery &&
    selectedAddress
  ) {
    orderPayload.customer = {
      ...pick(user, ['email', 'first_name', 'last_name', 'phone']),
      id: user.id ?? null,
      delivery: {
        // @ts-expect-error update when order api types are verified
        address: {
          // this flag needs to be present for new true addresses.
          // old addresses should pass through existing values from the API
          is_google_place: true,
          ...selectedAddress,
        },
      },
    };
  }

  if (order.fulfill_method === FULFILLMENT_METHODS.curbside) {
    orderPayload.customer = {
      ...pick(user, ['email', 'first_name', 'last_name', 'phone']),
      id: user.id ?? null,
      curbside: order.vehicle ? { vehicle: order.vehicle } : null, // Hermosa's json schema doesn't accept vehicle as null
      delivery: null, // json schema expects delivery to be null
    };
  }

  if (order.fulfill_method === FULFILLMENT_METHODS.dineIn) {
    orderPayload.customer = {
      ...pick(user, ['email', 'first_name', 'last_name', 'phone']),
      id: user?.id ?? null,
      curbside: null, // json schema expects curbside to be null
      delivery: null, // json schema expects delivery to be null
      dine_in: order.table_name ? { table_name: order.table_name } : null,
    };
  }

  if (isYelpPlatform()) {
    orderPayload.meta.marketplace = {
      id: '1',
      x_id: getYelpData().opportunity_token,
    };
  }

  return orderPayload;
}
